import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiLoader } from 'react-icons/fi';
import { API_BASE_URL, toastdisplay } from '../utils/utils';
import { getStoredUser, setAuthenticatedSession } from '../utils/authSession';
import { getPremiumLandingPath } from '../utils/subscriptionProductScope';
import { getErrorMessage } from '../utils/getErrorMessage';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(getStoredUser());
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  useEffect(() => {
    const verifyPayment = async () => {
      console.log('we are here');
      try {
        setLoading(true);

        // Get pending subscription info
        const pendingSubscription = JSON.parse(
          localStorage.getItem('pending_subscription') || '{}'
        );

        if (!pendingSubscription.user_email) {
          throw new Error(
            'Failed to verify payment status. If you have been charged, please contact us at contact@liwaza.org.'
          );
        }

        // Check if user's premium status has been updated
        const response = await fetch(
          `${API_BASE_URL}/api/user/profile?userEmail=${pendingSubscription.user_email}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            'Failed to verify payment status. If you have been charged, please contact us at contact@liwaza.org'
          );
        }

        const data = await response.json();
        const userData = data.user;
        if (userData.is_premium) {
          // Update user in session storage
          setAuthenticatedSession(userData);

          // Clear pending subscription
          localStorage.removeItem('pending_subscription');

          setSuccess(true);
          toastdisplay(
            'Payment successful! Welcome to Liwaza Premium!',
            true,
            3000,
            setToast
          );

          // Set flag to show onboarding and redirect to home after 3 seconds
          setTimeout(() => {
            sessionStorage.setItem('fromPricing', 'true');
            navigate(getPremiumLandingPath(userData));
          }, 3000);
        } else {
          // Payment might still be processing, wait a bit and try again
          setError(
            'Unable to verify payment. If you have been charged, please contact us at contact@liwaza.org.'
          );
          setTimeout(() => {
            verifyPayment();
          }, 4000);
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        setError(
          'Unable to verify payment. If you have been charged, please contact us at contact@liwaza.org.'
        );
        toastdisplay('Payment verification failed', false, 3000, setToast);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [navigate, user]);

  if (loading) {
    return (
      <div className="payment-success-container">
        <div className="payment-success-card">
          <FiLoader className="payment-icon spinning" size={48} />
          <h1>Verifying Payment...</h1>
          <p>Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-container">
        <div className="payment-success-card error">
          <h1>Payment Verification Failed</h1>
          <p>{getErrorMessage(error, 'Payment verification failed.')}</p>
          <button
            className="btn-lwz-primary"
            onClick={() => navigate('/pricing')}
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          <span className="toast-icon">{toast.success ? '✔️' : '❌'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      <div className="payment-success-card">
        <FiCheckCircle className="payment-icon success" size={48} />
        <h1>Payment Successful!</h1>
        <p>
          Welcome to Liwaza Premium! You now have access to all premium
          features.
        </p>
        <p>Redirecting you to the community...</p>

        <div className="success-features">
          <h3>You now have access to:</h3>
          <ul>
            <li>AI coaching for GRE and IELTS</li>
            <li>Expert profile building</li>
            <li>Mock interview sessions</li>
            <li>Community forum access</li>
            <li>Priority customer support</li>
          </ul>
        </div>

        <button
          className="btn-lwz-primary"
          onClick={() => {
            sessionStorage.setItem('fromPricing', 'true');
            const u = JSON.parse(sessionStorage.getItem('user') || 'null');
            navigate(getPremiumLandingPath(u));
          }}
        >
          Go to Home
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
